import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  api,
  type ApiError,
  type GetSegmentSchemaResult,
  type PreviewSegmentArgs,
  type PreviewSegmentResult,
} from '@whinerp/client';
import { EmptyState, ErrorState, Num, PageHeading, Panel, RuleBuilder, SectionLabel, Skeleton } from '@whinerp/ui';
import type { RuleNode, RuleSchema } from '@whinerp/contracts/rules';
import { isFieldType } from '@whinerp/contracts/fields';
import { formatCount } from '@whinerp/contracts/format';
import { useFormat, useT } from '../../app/i18n.js';
import { defineScreen } from '../types.js';
import { ruleBuilderLabels } from '../catalog/rule-labels.js';

/**
 * SC-19 Segment builder.
 *
 * The third caller of the one RuleBuilder, and the one where the live count
 * matters most: an audience is a thing somebody is about to send a campaign
 * to, and "did I describe the set I meant" is a question only a count and a
 * few names can answer.
 *
 * Both come from the server, over the same conditions, in one request - so the
 * count and the examples cannot be about different sets.
 */

function SegmentBuilderScreen(): JSX.Element {
  const t = useT();
  const format = useFormat();

  const [conditions, setConditions] = useState<RuleNode>({
    all: [{ field: 'title', operator: 'is-set' }],
  });

  const schema = useQuery({
    queryKey: ['segment-schema'],
    queryFn: () => api.getSegmentSchema({}),
  });

  const preview = useQuery<PreviewSegmentResult, ApiError>({
    queryKey: ['segment-preview', JSON.stringify(conditions)],
    queryFn: () =>
      api.previewSegment({ body: { objectKey: 'contact', conditions: toWire(conditions) } }),
  });

  if (schema.isError) return <SegmentError error={schema.error} retry={() => void schema.refetch()} />;
  if (schema.isPending) return <SegmentLoading />;

  return (
    <div data-component="SegmentBuilder" className="flex w-full flex-col gap-4">
      <PageHeading>{t('segment.title')}</PageHeading>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[65%_35%]">
        <Panel data-region="conditions">
          <RuleBuilder
            schema={toRuleSchema(schema.data)}
            value={conditions}
            onChange={setConditions}
            labels={ruleBuilderLabels(t)}
            // Null while a request is in flight: "not counted yet" and "nobody
            // matches" are different facts, and showing zero for both teaches
            // people to ignore the number.
            matchCount={preview.isFetching ? null : (preview.data?.count ?? null)}
            sampleRows={(preview.data?.sample ?? []).map((entry) => ({
              id: entry.id,
              label: entry.label,
            }))}
          />
        </Panel>

        <Panel data-region="count">
          <div className="flex flex-col gap-2">
            <SectionLabel>{t('segment.membersLabel')}</SectionLabel>
            {preview.isPending ? (
              <Skeleton className="h-7 w-block-sm" />
            ) : (
              <Num data-segment-count className="text-h1 text-ink">
                {t('segment.members', {
                  values: { count: formatCount(preview.data?.count ?? 0, format) },
                })}
              </Num>
            )}

            <SectionLabel>{t('segment.sample')}</SectionLabel>
            <ul data-segment-sample className="flex flex-col">
              {(preview.data?.sample ?? []).map((entry) => (
                <li key={entry.id} className="border-t border-t-rule py-1 text-dense text-ink first:border-t-0">
                  {entry.label}
                </li>
              ))}
            </ul>
          </div>
        </Panel>
      </div>
    </div>
  );
}

/**
 * The published schema, read as a RuleSchema.
 *
 * The wire type says `type: string` because the API publishes it that way;
 * the builder wants the field-type union. Narrowed here in one place rather
 * than cast through `unknown` at the call site (AR-21) - and a type the server
 * sends that is not a field type would be a schema nobody could build against
 * anyway, so it becomes text.
 */
/**
 * The document, as the wire type names it.
 *
 * `RuleNode` and the published `RuleDocument` are the same shape written twice
 * - once recursively in contracts, once flattened to three levels for OpenAPI,
 * which cannot express recursion. The conversion is this one function rather
 * than a cast at each call site, and it is where a fourth level would fail to
 * compile, which is the level `validateRule` refuses anyway.
 */
function toWire(node: RuleNode): PreviewSegmentArgs['body']['conditions'] {
  return node as PreviewSegmentArgs['body']['conditions'];
}

function toRuleSchema(published: GetSegmentSchemaResult): RuleSchema {
  return {
    key: published.key,
    label: published.label,
    outcome: published.outcome,
    maxDepth: published.maxDepth,
    // An audience of everybody is stated, never left blank.
    emptyMeans: 'nothing',
    fields: published.fields.map((field) => ({
      key: field.key,
      label: field.label,
      type: isFieldType(field.type) ? field.type : 'text',
      ...(field.help ? { help: field.help } : {}),
    })),
  };
}

function SegmentEmpty(): JSX.Element {
  const t = useT();
  return (
    <EmptyState
      reason="no-records"
      illustration="Users"
      title={t('segment.empty.title')}
      body={t('segment.empty.body')}
      primary={{ label: t('empty.action.addRule'), icon: 'Plus', href: '/campaigns' }}
    />
  );
}

function SegmentLoading(): JSX.Element {
  const t = useT();
  return (
    <div className="flex w-full flex-col gap-4" role="status" aria-live="polite" aria-busy>
      <span className="sr-only">{t('state.loading')}</span>
      <Skeleton className="h-line-h1 w-block-md" />
      <Skeleton className="h-block-xl w-full" />
    </div>
  );
}

function SegmentError({ error, retry }: { error: unknown; retry: () => void }): JSX.Element {
  return (
    <ErrorState
      title="The audience could not be loaded"
      body={error instanceof Error ? error.message : 'It did not load.'}
      onRetry={retry}
    />
  );
}

export const screen = defineScreen({
  id: 'SC-19',
  route: '/segments/:id',
  surface: 'page',
  title: 'Segment builder',
  features: ['F-063'],
  entitlement: 'F-063',
  reachedBy: [{ kind: 'setup', section: 'audiences' }],
  components: ['RuleBuilder'],
  elements: ['SegmentBuilder'],
  sampleParams: { id: 'new' },
  states: {
    empty: SegmentEmpty,
    loading: SegmentLoading,
    error: SegmentError,
  },
  component: SegmentBuilderScreen,
});

export default screen;
